import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  useTheme,
  Skeleton,
  Divider
} from '@mui/material';
import { Warehouse, Inbox } from '@mui/icons-material';
import { useDefaultWarehouseStatus } from '../hooks/useDashboardData';

export const ReceivingAreaStatus: React.FC = () => {
  const theme = useTheme();
  const { data, isLoading, error } = useDefaultWarehouseStatus();

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warehouse />
            <Typography variant="h4" fontWeight={700}>
              Receiving Area
            </Typography>
          </Box>
        }
        subheader={
          !isLoading && data && (
            <Typography variant="body2" color="text.secondary">
              {data.warehouseName} ({data.warehouseCode})
            </Typography>
          )
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {isLoading ? (
          <Box>
            <Skeleton width="100%" height={40} sx={{ mb: 2 }} />
            {Array.from(new Array(3)).map((_, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Skeleton width="80%" sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Typography color="error">Failed to load data</Typography>
        ) : data ? (
          <Box>
            {/* Summary Stats */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-around',
                mb: 3,
                p: 2,
                borderRadius: theme.general.borderRadiusSm,
                backgroundColor: theme.colors.alpha.black[5]
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {data.totalQuantity.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Units
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="secondary">
                  {data.uniqueUnits}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Unique SKUs
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700}>
                  {data.storageUnitsWithInventory}/{data.storageUnitsCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Bays in Use
                </Typography>
              </Box>
            </Box>

            {/* Storage Units Breakdown */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Storage Units
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.storageUnits && Array.isArray(data.storageUnits) && data.storageUnits
                .filter((unit) => unit.quantity > 0)
                .slice(0, 5)
                .map((unit) => {
                  const percentage = (unit.quantity / data.totalQuantity) * 100;
                  return (
                    <Box key={unit.storageUnitId}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 0.5
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Inbox
                            sx={{
                              fontSize: '1rem',
                              color: unit.isReceptionUnit
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main
                            }}
                          />
                          <Box>
                            <Typography variant="caption" fontWeight={600}>
                              {unit.storageUnitName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {unit.storageUnitCode}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" fontWeight={600}>
                            {unit.quantity.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {unit.uniqueUnits} SKUs
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(percentage, 100)}
                        sx={{
                          height: 6,
                          borderRadius: theme.general.borderRadiusSm,
                          backgroundColor: theme.colors.alpha.black[10],
                          '& .MuiLinearProgress-bar': {
                            borderRadius: theme.general.borderRadiusSm,
                            backgroundColor: unit.isReceptionUnit
                              ? theme.palette.primary.main
                              : theme.palette.secondary.main
                          }
                        }}
                      />
                    </Box>
                  );
                })}
            </Box>

            {data.storageUnits && Array.isArray(data.storageUnits) && data.storageUnits.filter((u) => u.quantity > 0).length > 5 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                +{data.storageUnits.filter((u) => u.quantity > 0).length - 5} more storage units
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">No data available</Typography>
        )}
      </CardContent>
    </Card>
  );
};
